/**
 * Achievements Module - Core Engine
 * Handles achievement calculation, progress tracking, and unlocking
 */

import { db, auth } from './firebase.js';
import { collection, doc, setDoc, getDoc, getDocs, updateDoc, onSnapshot, deleteDoc, query, where, orderBy, limit, writeBatch } from 'firebase/firestore';
import { ACHIEVEMENT_DEFINITIONS, getAllAchievementIds, getAchievementById } from './achievementTypes.js';

/**
 * Achievement Progress Tracker
 * Manages real-time achievement progress and unlocking
 */
class AchievementEngine {
  constructor() {
    this.userId = null;
    this.progressData = null;
    this.unsubscribers = [];
    this.achievementListeners = [];
  }

  /**
   * Initialize achievement engine for current user
   */
  async initialize(user) {
    if (!user) {
      console.log('No user provided to achievement engine');
      return;
    }

    this.userId = user.uid;
    console.log(`Initializing achievement engine for user: ${this.userId}`);

    try {
      // Initialize user achievement progress if it doesn't exist
      await this.initializeUserProgress();
      
      // Set up real-time listeners
      this.setupProgressListener();
      
      // Perform initial achievement check
      await this.calculateAllAchievements();
      
      console.log('Achievement engine initialized successfully');
    } catch (error) {
      console.error('Error initializing achievement engine:', error);
    }
  }

  /**
   * Initialize user progress document if it doesn't exist
   */
  async initializeUserProgress() {
    const progressRef = doc(db, 'users', this.userId, 'achievementProgress', 'progress');
    const progressDoc = await getDoc(progressRef);

    if (!progressDoc.exists()) {
      const initialProgress = {
        userId: this.userId,
        activeStreaks: {
          consecutiveMonthsUnderBudget: 0,
          dailyExpenseLogging: 0,
          noUnplannedExpenses: 0,
          consecutiveDaysExpenseLogging: 0
        },
        cumulativeStats: {
          totalMonthsUnderBudget: 0,
          totalSavedUnderBudget: 0,
          totalPlanningIncome: 0,
          totalPlanningSurplus: 0,
          totalExpensesLogged: 0,
          totalDaysActive: 0,
          monthsCompleted: 0,
          precisionMonths: 0,
          budgetsCreated: 0,
          lastMonthBudgetStatus: null
        },
        recentMilestones: [],
        nextMilestones: [],
        lastCalculated: new Date(),
        createdAt: new Date()
      };

      await setDoc(progressRef, initialProgress);
      console.log('Initialized achievement progress for user');
    }

    // Initialize user achievements for each achievement definition
    await this.initializeUserAchievements();
  }

  /**
   * Initialize user achievement records for all defined achievements
   */
  async initializeUserAchievements() {
    // One read to find which achievements already exist
    const achievementsRef = collection(db, 'users', this.userId, 'achievements');
    const snapshot = await getDocs(achievementsRef);
    const existingIds = new Set(snapshot.docs.map(d => d.id));

    const batch = writeBatch(db);
    let batchHasWrites = false;

    for (const achievementId of getAllAchievementIds()) {
      if (existingIds.has(achievementId)) continue;

      const achievement = getAchievementById(achievementId);
      const userAchievementRef = doc(db, 'users', this.userId, 'achievements', achievementId);
      batch.set(userAchievementRef, {
        userId: this.userId,
        achievementId,
        status: 'not_started',
        progress: {
          currentValue: 0,
          targetValue: achievement.requirements.threshold,
          percentage: 0,
          lastUpdated: new Date()
        },
        earnedAt: null,
        streakData: achievement.type === 'streak' ? {
          currentStreak: 0,
          bestStreak: 0,
          streakStartDate: null
        } : null,
        shareCount: 0,
        isHidden: false,
        metadata: {},
        createdAt: new Date()
      });
      batchHasWrites = true;
    }

    if (batchHasWrites) {
      await batch.commit();
    }
    console.log('Initialized user achievements');
  }

  /**
   * Set up real-time listener for progress changes
   */
  setupProgressListener() {
    const progressRef = doc(db, 'users', this.userId, 'achievementProgress', 'progress');
    
    const unsubscribe = onSnapshot(progressRef, (doc) => {
      if (doc.exists()) {
        this.progressData = doc.data();
        console.log('Achievement progress updated:', this.progressData);
        
        // Notify listeners about progress changes
        this.notifyProgressListeners();
      }
    });

    this.unsubscribers.push(unsubscribe);
  }

  /**
   * Add listener for achievement updates
   */
  addAchievementListener(callback) {
    this.achievementListeners.push(callback);
  }

  /**
   * Remove achievement listener
   */
  removeAchievementListener(callback) {
    const index = this.achievementListeners.indexOf(callback);
    if (index > -1) {
      this.achievementListeners.splice(index, 1);
    }
  }

  /**
   * Notify all listeners about progress changes
   */
  notifyProgressListeners() {
    this.achievementListeners.forEach(callback => {
      try {
        callback(this.progressData);
      } catch (error) {
        console.error('Error in achievement listener:', error);
      }
    });
  }

  /**
   * Update progress for a specific metric
   */
  async updateProgress(metric, value, metadata = {}) {
    if (!this.userId) {
      console.warn('Cannot update progress: no user logged in');
      return;
    }

    // Wait for current user authentication
    if (!auth.currentUser) {
      console.warn('Cannot update progress: authentication not ready');
      return;
    }

    try {
      const progressRef = doc(db, 'users', this.userId, 'achievementProgress', 'progress');

      // this.progressData is kept live by the onSnapshot listener; only fall back
      // to a getDoc if we somehow reach here before the first snapshot fires
      if (!this.progressData) {
        const progressDoc = await getDoc(progressRef);
        if (!progressDoc.exists()) {
          await this.initializeUserProgress();
        } else {
          this.progressData = progressDoc.data();
        }
      }

      // Update the specific metric
      const updates = {
        lastCalculated: new Date()
      };

      // Handle different types of metric updates
      if (metric.startsWith('activeStreaks.')) {
        const streakKey = metric.replace('activeStreaks.', '');
        updates[`activeStreaks.${streakKey}`] = value;
      } else if (metric.startsWith('cumulativeStats.')) {
        const statKey = metric.replace('cumulativeStats.', '');
        updates[`cumulativeStats.${statKey}`] = value;
      }

      // Add metadata if provided
      if (metadata && Object.keys(metadata).length > 0) {
        updates.metadata = metadata;
      }

      await updateDoc(progressRef, updates);
      console.log(`Updated progress metric ${metric} to ${value}`);

      // Trigger achievement calculation for related achievements
      await this.calculateRelevantAchievements(metric);

    } catch (error) {
      console.error('Error updating progress:', error);
    }
  }

  /**
   * Calculate achievements relevant to a specific metric
   */
  async calculateRelevantAchievements(metric) {
    // Filter to matching IDs in memory (no Firestore reads)
    const relevantIds = getAllAchievementIds().filter(id =>
      this.isMetricRelevant(getAchievementById(id), metric)
    );

    if (relevantIds.length === 0) return;

    // Fetch all relevant docs in parallel, then process
    const docs = await Promise.all(
      relevantIds.map(id => getDoc(doc(db, 'users', this.userId, 'achievements', id)))
    );

    for (let i = 0; i < relevantIds.length; i++) {
      await this.calculateSingleAchievement(relevantIds[i], docs[i]);
    }
  }

  /**
   * Check if a metric is relevant to an achievement
   */
  isMetricRelevant(achievement, metric) {
    const achievementMetric = achievement.requirements.metric;
    
    // Map frontend metrics to achievement requirements
    const metricMappings = {
      'activeStreaks.consecutiveMonthsUnderBudget': ['consecutive_months_under_budget'],
      'activeStreaks.consecutiveDaysExpenseLogging': ['consecutive_days_expense_logging'],
      'cumulativeStats.totalSavedUnderBudget': ['total_saved_under_budget'],
      'cumulativeStats.totalPlanningIncome': ['cumulative_planning_surplus'],
      'cumulativeStats.totalPlanningSurplus': ['cumulative_planning_surplus'],
      'cumulativeStats.monthsCompleted': ['first_month_completed'],
      'cumulativeStats.budgetsCreated': ['first_budget_created'],
      'cumulativeStats.totalMonthsUnderBudget': ['total_months_under_budget']
    };

    const relevantMetrics = metricMappings[metric] || [];
    return relevantMetrics.includes(achievementMetric);
  }

  /**
   * Calculate progress for a single achievement.
   * @param {string} achievementId
   * @param {DocumentSnapshot|null} prefetchedDoc - pre-fetched Firestore doc to avoid an extra read
   */
  async calculateSingleAchievement(achievementId, prefetchedDoc = null) {
    try {
      const achievement = getAchievementById(achievementId);
      if (!achievement) {
        console.warn(`Achievement not found: ${achievementId}`);
        return;
      }

      const userAchievementRef = doc(db, 'users', this.userId, 'achievements', achievementId);
      const userAchievementDoc = prefetchedDoc ?? await getDoc(userAchievementRef);

      if (!userAchievementDoc.exists()) {
        console.warn(`User achievement not found: ${achievementId}`);
        return;
      }

      const userAchievement = userAchievementDoc.data();
      
      // Skip if already completed
      if (userAchievement.status === 'completed') {
        return;
      }

      // Get current value for this achievement's metric
      const currentValue = await this.getCurrentMetricValue(achievement.requirements.metric);
      const targetValue = achievement.requirements.threshold;
      const percentage = Math.min((currentValue / targetValue) * 100, 100);

      // Determine new status
      let newStatus = userAchievement.status;
      let earnedAt = userAchievement.earnedAt;

      if (currentValue >= targetValue && userAchievement.status !== 'completed') {
        newStatus = 'completed';
        earnedAt = new Date();
        
        // Trigger achievement unlock notification
        await this.triggerAchievementUnlock(achievementId, achievement);
      } else if (currentValue > 0 && userAchievement.status === 'not_started') {
        newStatus = 'in_progress';
      }

      // Update user achievement
      const updates = {
        status: newStatus,
        'progress.currentValue': currentValue,
        'progress.targetValue': targetValue,
        'progress.percentage': percentage,
        'progress.lastUpdated': new Date()
      };

      if (earnedAt) {
        updates.earnedAt = earnedAt;
      }

      // Update streak data if applicable
      if (achievement.type === 'streak') {
        updates['streakData.currentStreak'] = currentValue;
        if (currentValue > (userAchievement.streakData?.bestStreak || 0)) {
          updates['streakData.bestStreak'] = currentValue;
        }
      }

      await updateDoc(userAchievementRef, updates);

      console.log(`Updated achievement ${achievementId}: ${currentValue}/${targetValue} (${percentage.toFixed(1)}%)`);

    } catch (error) {
      console.error(`Error calculating achievement ${achievementId}:`, error);
    }
  }

  /**
   * Get current value for a specific metric
   */
  async getCurrentMetricValue(metric) {
    if (!this.progressData) {
      const progressRef = doc(db, 'users', this.userId, 'achievementProgress', 'progress');
      const progressDoc = await getDoc(progressRef);
      this.progressData = progressDoc.exists() ? progressDoc.data() : {};
    }

    switch (metric) {
      case 'consecutive_months_under_budget':
        return this.progressData.activeStreaks?.consecutiveMonthsUnderBudget || 0;
      
      case 'consecutive_days_expense_logging':
        return this.progressData.activeStreaks?.consecutiveDaysExpenseLogging || 0;
      
      case 'total_saved_under_budget':
        return this.progressData.cumulativeStats?.totalSavedUnderBudget || 0;
      
      case 'cumulative_planning_surplus':
        // Calculate percentage of cumulative surplus vs cumulative income
        const totalIncome = this.progressData.cumulativeStats?.totalPlanningIncome || 0;
        const totalSurplus = this.progressData.cumulativeStats?.totalPlanningSurplus || 0;
        return totalIncome > 0 ? (totalSurplus / totalIncome) : 0;
      
      case 'first_month_completed':
        return this.progressData.cumulativeStats?.monthsCompleted || 0;
      
      case 'first_budget_created':
        return this.progressData.cumulativeStats?.budgetsCreated || 0;
      
      case 'total_months_under_budget':
        return this.progressData.cumulativeStats?.totalMonthsUnderBudget || 0;
      
      case 'budget_variance_percentage':
        // This metric should be calculated dynamically from current budget data
        // For now, return 0 and let the calling function calculate it
        console.log('budget_variance_percentage requires dynamic calculation');
        return 0;
      
      case 'monthly_categorization_percentage':
        // This metric should be calculated dynamically from current expense data
        // For now, return 0 and let the calling function calculate it
        console.log('monthly_categorization_percentage requires dynamic calculation');
        return 0;
      
      case 'precision_months':
        return this.progressData.cumulativeStats?.precisionMonths || 0;
      
      default:
        console.warn(`Unknown metric: ${metric}`);
        return 0;
    }
  }

  /**
   * Trigger achievement unlock notification and celebration
   */
  async triggerAchievementUnlock(achievementId, achievement) {
    try {
      console.log(`🎉 Achievement unlocked: ${achievement.name}!`);

      // Use the in-memory progressData kept live by onSnapshot
      const progressRef = doc(db, 'users', this.userId, 'achievementProgress', 'progress');
      const recentMilestones = (this.progressData?.recentMilestones || []).slice();
      recentMilestones.unshift({
        achievementId,
        achievementName: achievement.name,
        unlockedAt: new Date(),
        tier: achievement.tier,
        points: achievement.rewards.points
      });

      // Keep only last 10 milestones
      const trimmedMilestones = recentMilestones.slice(0, 10);

      await updateDoc(progressRef, {
        recentMilestones: trimmedMilestones
      });

      // Trigger UI notification
      this.showAchievementNotification(achievement);

    } catch (error) {
      console.error('Error triggering achievement unlock:', error);
    }
  }

  /**
   * Show achievement notification in UI
   */
  showAchievementNotification(achievement) {
    // This will be implemented when we add the UI components
    console.log(`UI Notification: ${achievement.name} unlocked!`);
    
    // For now, we'll dispatch a custom event that the UI can listen for
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('achievementUnlocked', {
        detail: {
          achievement,
          timestamp: new Date()
        }
      });
      window.dispatchEvent(event);
    }
  }

  /**
   * Calculate all achievements for current user
   */
  async calculateAllAchievements() {
    // One read to load all achievement docs, then process in memory
    const snapshot = await getDocs(collection(db, 'users', this.userId, 'achievements'));
    const achievementDocMap = new Map(snapshot.docs.map(d => [d.id, d]));

    for (const achievementId of getAllAchievementIds()) {
      await this.calculateSingleAchievement(achievementId, achievementDocMap.get(achievementId) ?? null);
    }

    console.log('Calculated all achievements');
  }

  /**
   * Get user's achievement progress
   */
  async getUserAchievements() {
    if (!this.userId) {
      return [];
    }

    try {
      const userAchievementsQuery = query(
        collection(db, 'users', this.userId, 'achievements'),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(userAchievementsQuery);
      const achievements = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        const achievement = getAchievementById(data.achievementId);
        if (achievement) {
          achievements.push({
            ...data,
            definition: achievement
          });
        }
      });

      return achievements;
    } catch (error) {
      console.error('Error getting user achievements:', error);
      return [];
    }
  }

  /**
   * Get achievement statistics for user
   */
  async getAchievementStats() {
    const userAchievements = await this.getUserAchievements();
    
    const stats = {
      totalBadges: userAchievements.filter(a => a.status === 'completed').length,
      totalPoints: userAchievements
        .filter(a => a.status === 'completed')
        .reduce((sum, a) => sum + (a.definition.rewards.points || 0), 0),
      categoryCounts: {},
      tierCounts: {},
      inProgress: userAchievements.filter(a => a.status === 'in_progress').length,
      notStarted: userAchievements.filter(a => a.status === 'not_started').length
    };

    // Count by category and tier
    userAchievements.filter(a => a.status === 'completed').forEach(achievement => {
      const category = achievement.definition.category;
      const tier = achievement.definition.tier;
      
      stats.categoryCounts[category] = (stats.categoryCounts[category] || 0) + 1;
      stats.tierCounts[tier] = (stats.tierCounts[tier] || 0) + 1;
    });

    return stats;
  }

  /**
   * Cleanup listeners when user logs out
   */
  cleanup() {
    this.unsubscribers.forEach(unsubscribe => unsubscribe());
    this.unsubscribers = [];
    this.achievementListeners = [];
    this.userId = null;
    this.progressData = null;
    console.log('Achievement engine cleaned up');
  }
}

// Create singleton instance
const achievementEngine = new AchievementEngine();

// Export the singleton instance and key functions
export default achievementEngine;

export {
  achievementEngine,
  AchievementEngine
};

/**
 * Convenience functions for integration with other modules
 */

// Budget completion tracking
export async function trackBudgetCompletion(userId, budgetData, monthKey) {
  const isUnderBudget = budgetData.totalIncome > budgetData.totalExpenses;
  const savedAmount = isUnderBudget ? budgetData.totalIncome - budgetData.totalExpenses : 0;
  
  // Update monthly completion
  await achievementEngine.updateProgress('cumulativeStats.monthsCompleted', 
    (achievementEngine.progressData?.cumulativeStats?.monthsCompleted || 0) + 1);
  
  // Update planning metrics (always track, not just when under budget)
  const planningSurplus = budgetData.totalIncome - budgetData.totalExpenses; // Can be negative
  const currentTotalIncome = (achievementEngine.progressData?.cumulativeStats?.totalPlanningIncome || 0) + budgetData.totalIncome;
  const currentTotalSurplus = (achievementEngine.progressData?.cumulativeStats?.totalPlanningSurplus || 0) + planningSurplus;
  
  await achievementEngine.updateProgress('cumulativeStats.totalPlanningIncome', currentTotalIncome);
  await achievementEngine.updateProgress('cumulativeStats.totalPlanningSurplus', currentTotalSurplus);
  
  if (isUnderBudget) {
    // Update consecutive months under budget
    const currentStreak = (achievementEngine.progressData?.activeStreaks?.consecutiveMonthsUnderBudget || 0) + 1;
    await achievementEngine.updateProgress('activeStreaks.consecutiveMonthsUnderBudget', currentStreak);
    
    // Update total saved
    const totalSaved = (achievementEngine.progressData?.cumulativeStats?.totalSavedUnderBudget || 0) + savedAmount;
    await achievementEngine.updateProgress('cumulativeStats.totalSavedUnderBudget', totalSaved);
    
    // Update total months under budget
    const totalMonths = (achievementEngine.progressData?.cumulativeStats?.totalMonthsUnderBudget || 0) + 1;
    await achievementEngine.updateProgress('cumulativeStats.totalMonthsUnderBudget', totalMonths);
  } else {
    // Reset consecutive months streak
    await achievementEngine.updateProgress('activeStreaks.consecutiveMonthsUnderBudget', 0);
  }
}

// Budget creation tracking
export async function trackBudgetCreation(userId) {
  const currentBudgetsCreated = (achievementEngine.progressData?.cumulativeStats?.budgetsCreated || 0) + 1;
  await achievementEngine.updateProgress('cumulativeStats.budgetsCreated', currentBudgetsCreated);
}

// Expense logging tracking
export async function trackExpenseLogging(userId) {
  // This will be called when expenses are logged
  // For now, we'll implement daily tracking
  const today = new Date().toDateString();
  const lastLogged = localStorage.getItem(`lastExpenseLogged_${userId}`);
  
  if (lastLogged !== today) {
    localStorage.setItem(`lastExpenseLogged_${userId}`, today);
    
    // Update consecutive days if logged yesterday, reset if gap
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    let currentStreak = 1;
    if (lastLogged === yesterday.toDateString()) {
      currentStreak = (achievementEngine.progressData?.activeStreaks?.consecutiveDaysExpenseLogging || 0) + 1;
    }
    
    await achievementEngine.updateProgress('activeStreaks.consecutiveDaysExpenseLogging', currentStreak);
  }
}